/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderNavigationService,
  BuilderUserContext,
  isJobEditable,
  JobDetailsModel,
} from '@gh/builder-shared';
import { BuilderPermission, JobSharedDocType } from '@gh/builder-shared-data';
import { dictionaryValueFormatter, GoogleDocument } from '@gh/common-internal-shared';
import { DateTimeTypes, FormState, FormStateBuilder, getRouteInitData } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService, translationTuple } from '@gh/core-mls';
import {
  DialogService,
  FormMode,
  NotificationService,
  PreventableEvent,
  STYLES_FLEX_COLUMN_LAYOUT,
  WaitingService,
  yesAnswer,
  yesCancelConfirmation,
} from '@gh/core-ui';
import { buildDateTimeColumn, GridDefinition, GridDefinitionFactory } from '@gh/core-ui-grid';
import { filter$, switchMap$ } from '@gh/rx/operators';
import { isEmpty, last, some } from 'lodash';
import {
  JobDetailsTabBusinessService,
  JobSharedDocListItem,
  JobSharedDocSet,
} from '../../../resources/job-details-tab';

@Component({
  standalone: false,
  selector: 'gh-job-details-shared-doc-list-tab',
  templateUrl: 'job-details-shared-doc-list-tab.component.html',
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
})
export class JobDetailsSharedDocListTabComponent implements OnInit {
  form: FormState<JobSharedDocSet>;
  gridDefinition: GridDefinition;
  jobId: ID;
  docs: JobSharedDocListItem[];
  docsLoading: boolean = false;

  constructor(private route: ActivatedRoute,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private userContext: BuilderUserContext,
              private gridDefinitionFactory: GridDefinitionFactory,
              private formStateBuilder: FormStateBuilder,
              private translations: TranslationService,
              private navigations: BuilderNavigationService,
              private waitings: WaitingService,
              private business: JobDetailsTabBusinessService,
              private model: JobDetailsModel) {
    this.validateSelectedDocuments = this.validateSelectedDocuments.bind(this);
  }

  get canShareDocs(): boolean {
    return some(this.form.group.controls, (control) => !isEmpty(control.value));
  }

  get folderId(): ID {
    return last(/\/([a-zA-Z0-9_-]+)$/.exec(this.model.basicInfo.jobDocsLink))!;
  }

  ngOnInit(): void {
    const { jobId, documents, sharedDocsTypeDict } = getRouteInitData(this.route);

    this.jobId = jobId;
    this.docs = documents;

    this.gridDefinition = this.gridDefinitionFactory.buildManual(
      [
        {
          field: 'sharedDate',
          headerName: this.translations.get('lbl_shared_date'),
          width: 180,
          suppressSizeToFit: true,
          ...buildDateTimeColumn(DateTimeTypes.DateTime),
        },
        { field: 'sharedBy', headerName: this.translations.get('lbl_shared_by'), width: 150, suppressSizeToFit: true },
        {
          field: 'name',
          headerName: this.translations.get('lbl_documents'),
          width: 150,
          sortable: false,
        },
        {
          field: 'docType',
          headerName: this.translations.get('lbl_type'),
          width: 150,
          sortable: false,
          suppressSizeToFit: true,
          valueFormatter: dictionaryValueFormatter(sharedDocsTypeDict),
        },
      ],
      {
        getRowId: (params) => String(params.data.id)
      });

    this.form = this.formStateBuilder.buildPlain({
      mode: this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.EDIT)
      && isJobEditable(this.model.basicInfo)
        ? FormMode.EditMode
        : FormMode.ViewMode,
      children: {
        contract: void 0,
        colorSelection: void 0,
        specification: void 0,
        coQuotations: void 0,
        plans: void 0,
        jobLetters: void 0,
      },
    });
  }

  onShareDocs(): void {
    this.business.shareDocuments(this.jobId, this.form.value).pipe(
      this.waitings.operator())
      .subscribe(
        () => {
          this.notifications.success('msg_docs_shared_successfully');
          this.form.group.reset();
          this.reloadDocuments();
        },
        this.notifications.processAll);
  }

  onUnshare(document: JobSharedDocListItem): void {
    this.dialogs.confirmation(yesCancelConfirmation('msg_unshare_doc')).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.business.unshareDocument(this.jobId, document.id).pipe(this.waitings.operator())))
      .subscribe(
        () => this.reloadDocuments(),
        this.notifications.processAll);
  }

  onValidateSingleDocument(event: PreventableEvent, verifiedType: JobSharedDocType): void {
    if (this.docs.some(({ docType }) => docType === verifiedType)) {
      event.preventDefault();
      this.notifications.error('msg_doc_already_shared');
    }
  }

  goToAel(): void {
    this.navigations.goToAel({
      entityId: this.jobId,
      entityType: 'JOB',
      label: translationTuple('lbl_job_number_with_param', { number: String(this.model.jobNumber) }),
    });
  }

  validateSelectedDocuments(docs: GoogleDocument[]): boolean {
    const isOnlyPdf = docs.every((doc) => doc.mimeType === 'application/pdf');
    if (!isOnlyPdf) {
      this.notifications.error('msg_e_document_format_error');
    }
    return isOnlyPdf;
  }

  private reloadDocuments(): void {
    this.docs = [];
    this.docsLoading = true;
    this.business.getSharedDocuments(this.jobId)
      .subscribe(
        (docs) => {
          this.docs = docs;
          this.docsLoading = false;
        },
        (error) => {
          this.docsLoading = false;
          this.notifications.processAll(error);
        });
  }
}
